/**
 * Self-intersection check for a 2D polygon ring. Used by GeoJson.tsx (live
 * during vertex editing) and AddPolygonTool (live during picking) so the
 * feedback model is consistent across both flows.
 *
 * The ring is given in open form: the closing duplicate vertex is implicit.
 * Adjacent edges share a vertex by construction and are not counted as
 * intersections; the seam pair (last edge → first edge) is likewise skipped.
 *
 * Algorithm: O(n²) signed-area orientation tests per CLRS §33. Cheap for the
 * ring sizes the picker produces (a couple of dozen verts at most).
 */
const EPS = 1e-9;

export const orient2D = (
  px: number,
  py: number,
  qx: number,
  qy: number,
  rx: number,
  ry: number,
) => {
  const v = (qx - px) * (ry - py) - (qy - py) * (rx - px);
  if (v > EPS) return 1;
  if (v < -EPS) return -1;
  return 0;
};

export const segmentsCross = (
  a1x: number,
  a1y: number,
  a2x: number,
  a2y: number,
  b1x: number,
  b1y: number,
  b2x: number,
  b2y: number,
) => {
  const o1 = orient2D(a1x, a1y, a2x, a2y, b1x, b1y);
  const o2 = orient2D(a1x, a1y, a2x, a2y, b2x, b2y);
  const o3 = orient2D(b1x, b1y, b2x, b2y, a1x, a1y);
  const o4 = orient2D(b1x, b1y, b2x, b2y, a2x, a2y);
  if (o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4) return true;
  const onSeg = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) =>
    Math.min(px, rx) - EPS <= qx &&
    qx <= Math.max(px, rx) + EPS &&
    Math.min(py, ry) - EPS <= qy &&
    qy <= Math.max(py, ry) + EPS;
  if (o1 === 0 && onSeg(a1x, a1y, b1x, b1y, a2x, a2y)) return true;
  if (o2 === 0 && onSeg(a1x, a1y, b2x, b2y, a2x, a2y)) return true;
  if (o3 === 0 && onSeg(b1x, b1y, a1x, a1y, b2x, b2y)) return true;
  if (o4 === 0 && onSeg(b1x, b1y, a2x, a2y, b2x, b2y)) return true;
  return false;
};

/**
 * Returns true iff the open (implicitly-closed) 2D ring has no
 * self-intersection. Rings with fewer than 4 vertices are always simple.
 */
export const isRingSimple2D = (ring: Array<[number, number]>) => {
  const n = ring.length;
  if (n < 4) return true;
  for (let i = 0; i < n; i++) {
    const [a1x, a1y] = ring[i];
    const [a2x, a2y] = ring[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      const [b1x, b1y] = ring[j];
      const [b2x, b2y] = ring[(j + 1) % n];
      if (segmentsCross(a1x, a1y, a2x, a2y, b1x, b1y, b2x, b2y)) return false;
    }
  }
  return true;
};

/**
 * Open-polyline version: treats the ring as a path (no implicit closure).
 * Used during polygon picking to check whether *appending* a candidate
 * vertex would create a self-intersection on any existing segment except
 * the one that shares the new vertex's predecessor.
 */
export const isPolylineSimple2D = (path: Array<[number, number]>) => {
  const n = path.length;
  if (n < 4) return true;
  for (let i = 0; i < n - 1; i++) {
    const [a1x, a1y] = path[i];
    const [a2x, a2y] = path[i + 1];
    for (let j = i + 2; j < n - 1; j++) {
      const [b1x, b1y] = path[j];
      const [b2x, b2y] = path[j + 1];
      if (segmentsCross(a1x, a1y, a2x, a2y, b1x, b1y, b2x, b2y)) return false;
    }
  }
  return true;
};
